import { Menu } from "antd";
import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useSettings } from "../../constants/settings";
import { useGlobalContext } from "../../context/GlobalContext";
import styled from "styled-components";
import {
  CashOutline,
  DocumentTextOutline,
  GridOutline,
  KeyOutline,
  LayersOutline,
  SaveOutline,
  SettingsOutline,
  ShieldCheckmarkOutline,
  WalletOutline,
} from "@raresail/react-ionicons";
import {
  URL_ACCOUNT_ALERTS,
  URL_AGENT_COMMISSION_SUMMARY,
  URL_AGENTS,
  URL_APPLICATION_USERS,
  URL_AUDIT_ACTIVITY,
  URL_BANK_ACCOUNTS,
  URL_CHARGE_ASSIGNMENT,
  URL_CHARTS_CONFIGURABLES,
  URL_CONTACT_US,
  URL_CREDIT_HOLDING_TYPES,
  URL_CREDIT_HOLDINGS,
  URL_DEBIT_HOLDINGS,
  URL_DEBIT_HOLDINGS_TYPES,
  URL_DYNAMIC_QR_CODE,
  URL_FRAUD_ALERTS,
  URL_FRAUD_AUDIT_LOGS,
  URL_FRAUD_CASES,
  URL_FRAUD_CONFIGURATIONS,
  URL_FRAUD_DASHBOARD,
  URL_FRAUD_EVENTS,
  URL_FRAUD_RULES_HISTORY,
  URL_FRAUD_TEMPLATES,
  URL_FRAUD_WEIGHTSCORE,
  URL_IFRAME_PAYMENTS,
  URL_INTERNAL_MERCHANT_ACCESS,
  URL_PARTNER_ITEM_MAPPING,
  URL_LEDGERS,
  URL_LIQUIDATION_BANK_CODES,
  URL_LIQUIDATIONS,
  URL_MARK_TRANSACTION,
  URL_MERCHANT_CODES,
  URL_MERCHANT_FEES,
  URL_MERCHANT_IP_WHITELIST,
  URL_MERCHANT_PAYMENTS,
  URL_MERCHANT_SERVICE_ACCESS,
  URL_MERCHANT_USERS,
  URL_MERCHANTS,
  URL_MONITORED_ACCOUNTS,
  URL_PARTNER_CHARGES,
  URL_PARTNER_COMMISSION_SUMMARY,
  URL_PARTNERS,
  URL_PAYMENT_ITEMS,
  URL_PAYMENT_SERVICES,
  URL_PAYMENT_TYPES,
  URL_PUBLIC_USERS,
  URL_QR_CODE_PAYMENT,
  URL_RECONCILIATION_STATEMENTS,
  URL_RECONCILIATIONS_REPORTS,
  URL_REJECTED_APPLICATION_USERS,
  URL_REPLENISHMENT,
  URL_REVERSE_TRANSACTIONS,
  URL_STAFF_USERS,
  URL_SYSTEM_WALLETS,
  URL_TRANSACTION_MONITORING,
  URL_TRIAL_BALANCE,
  URL_USERS,
  URL_WALLET_TRANSFERS,
  URL_YOYA_CHARGES,
  URL_YOYA_COMMISSION_SUMMARY,
  URL_UTILITY_TRANSACTIONS,
  URL_TRANSACTION_CHANNEL_SEPARATION,
  URL_PAYMENT_ITEM_CHANNEL_REPORT,
  URL_FLOAT_INTEREST_CONFIGS,
  URL_FLOAT_INTEREST_STATEMENTS,
  URL_TRANSACTION_SUMMARY,
  URL_SETTLEMENT_BATCHES,
  URL_SETTLEMENT_RULES,
  URL_SETTLEMENT_BENEFICIARIES,
  URL_SETTLEMENT_APPROVALS,
  URL_SECURITY_ACTIVITY,
  URL_BUSINESS_AUDIT_LOGS,
  URL_MERCHANT_SUMMARY,
  URL_PAYMENT_ITEM_SUMMARY,
  URL_INCIDENT_TICKETING,
  URL_TICKET_CATEGORIZATION,
  URL_RAISED_TICKETS,
  URL_ASSIGNED_TICKETS,
  URL_INCIDENT_TICKET_CATEGORY,
  URL_INCIDENT_TICKET_SEVERITY,
  URL_INCIDENT_TICKET_CATEGORY_REPORT,
  URL_INCIDENT_TICKET_SEVERITY_REPORT,
} from "../../utils/urls";
import {
  getForm,
  getRegister,
  itemExists,
  useGetUserPermissions,
} from "../../utils/helpers";
import {
  APPROVED,
  CAN_ADMIN,
  CAN_AGENT,
  CAN_CREATE_FEES,
  CAN_MERCHANT,
  DRAFT,
  PENDING_APPROVAL,
  SENT_TO_BANK,
  SETTLED,
  FAILED,
} from "../../utils/constants";
import {
  Add,
  AssessmentOutlined,
  BusinessOutlined,
  CastOutlined,
  DatasetLinkedOutlined,
  Diversity1Outlined,
  Diversity2Outlined,
  Group,
  NearMeOutlined,
  PasswordOutlined,
  PeopleOutline,
  SettingsBackupRestoreOutlined,
  ThreePOutlined,
  AccountTreeOutlined,
  StackedBarChartOutlined,
  CategoryOutlined,
  PeopleAltOutlined,
  WbIridescent,
  BookmarkAddOutlined,
  CreditCardOutlined,
  RedeemOutlined,
  PaymentOutlined,
  ListSharp,
  LinearScale,
  SettingsSystemDaydream,
  AssignmentOutlined,
  QrCode2Outlined,
  QrCodeScannerOutlined,
  MoneyOffOutlined,
  BrowseGalleryOutlined,
  CasesOutlined,
  ArticleOutlined,
  WorkHistoryOutlined,
  EventOutlined,
  SensorOccupiedOutlined,
  SupportAgentOutlined,
  StorefrontOutlined,
  Scale,
  SettingsInputComponentOutlined,
  PersonOff,
  Web,
  PaidOutlined,
  ConfirmationNumberOutlined,
  MoneyOutlined,
  TvOutlined,
  NotificationsActiveOutlined,
  CreditScoreOutlined,
  AccountBalanceOutlined,
  PaymentsTwoTone,
  LocalMallOutlined,
  RequestQuoteOutlined,
  ListAltOutlined,
  CallSplit,
  CurrencyExchange,
  ShareOutlined,
  ReportOutlined,
  EngineeringOutlined,
  NotListedLocationOutlined,
  BatchPredictionOutlined,
  SaveAsOutlined,
  PendingOutlined,
  VerifiedOutlined,
  SendOutlined,
  CheckCircleOutline,
  ErrorOutline,
  ApprovalOutlined,
  QueueOutlined,
  SettingsSuggestOutlined,
  DescriptionOutlined,
  SummarizeOutlined,
  SecurityOutlined,
  HelpOutline,
  BugReportOutlined,
} from "@mui/icons-material";
import { useModals } from "../common/useModals";
import { flattenMenu } from "../../utils/menuUtils";
import { useDynamicPageTitle } from "../../hooks/dynamic_title/useDynamicPageTitle";
import { useAuth } from "../../context/AuthContext";

/* ─── Notification dot helper ── */
const Dot = styled.span<{ $color?: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color ?? "#f97316"};
  display: inline-block;
  margin-left: auto;
  flex-shrink: 0;
`;

/* ─── Section label ── */
const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.28);
  padding: 16px 12px 5px;
  user-select: none;
`;

/* ─── Sidebar shell ── */
const SidebarShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #0d1b3e;
  position: relative;

  /* subtle right border */
  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const LogoArea = styled.div`
  padding: 18px 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LogoBadge = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: -0.5px;
`;

const LogoText = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.3px;
  line-height: 1;
`;

const MenuScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 8px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 4px;
  }
`;

const UserArea = styled.div`
  padding: 12px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div<{ $color?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(p) => p.$color ?? "#1b75bb"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
const UserName = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const UserRole = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.42);
  margin-top: 1px;
`;

const OnlineDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 2px #22c55e33;
  flex-shrink: 0;
`;

/* ─────────────────────────────────────────────────────────────────
   MENU DEFINITION — items grouped into sidebar sections
   ───────────────────────────────────────────────────────────────── */
function LeftMenuItems(): any {
  const permissions = useGetUserPermissions();
  const { is_staff }: any = useAuth();
  const {
    setOpenGenerateApiKey,
    setOpenStaticQRCode,
    setOpenIframeKeyGeneration,
    setOpenSettlementEligibilityReview,
  } = useModals();

  const menu: any = [
    // ── OVERVIEW ──────────────────────────────────────────────────
    { _section: "Overview" },
    {
      label: "Dashboard",
      to: "/",
      visible: true,
      key: "default",
      icon: <GridOutline />,
    },

    // ── USERS & MERCHANTS ─────────────────────────────────────────
    { _section: "Users & Merchants" },
    {
      label: "User Management",
      icon: <PeopleOutline />,
      to: getRegister(URL_USERS),
      visible: itemExists(permissions, [CAN_ADMIN]),
      dot: true,
      children: [
        { label: "Add User", icon: <Add />, to: getForm(URL_USERS) },
        {
          label: "Pending Applications",
          icon: <SensorOccupiedOutlined />,
          to: getRegister(URL_APPLICATION_USERS),
        },
        {
          label: "Rejected Applications",
          icon: <PersonOff />,
          to: getRegister(URL_REJECTED_APPLICATION_USERS),
        },
        {
          label: "Users",
          icon: <Group />,
          children: [
            { label: "All Users", icon: <Group />, to: getRegister(URL_USERS) },
            {
              label: "Staffs",
              icon: <Group />,
              to: getRegister(URL_STAFF_USERS),
            },
            {
              label: "Agents",
              icon: <SupportAgentOutlined />,
              to: getRegister(URL_AGENTS),
            },
            {
              label: "Merchants",
              icon: <StorefrontOutlined />,
              to: getRegister(URL_MERCHANTS),
            },
            {
              label: "Public Users",
              icon: <Group />,
              to: getRegister(URL_PUBLIC_USERS),
            },
          ],
        },
        {
          label: "Internal Merchant Access",
          icon: <PasswordOutlined />,
          to: getRegister(URL_INTERNAL_MERCHANT_ACCESS),
        },
        {
          label: "User Audit Trail",
          icon: <ShieldCheckmarkOutline />,
          to: getRegister(URL_AUDIT_ACTIVITY),
        },
      ],
    },
    {
      label: "Merchant Management",
      icon: <Diversity1Outlined />,
      visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
      children: [
        {
          label: "Merchant Users",
          icon: <Group />,
          to: getRegister(URL_MERCHANT_USERS),
        },
        {
          label: "Merchant Fees",
          icon: <BusinessOutlined />,
          to: getRegister(URL_MERCHANT_FEES),
          visible: itemExists(permissions, [CAN_CREATE_FEES]),
        },
        {
          label: "Encryption Key Generation",
          icon: <KeyOutline />,
          click: () => setOpenGenerateApiKey(),
        },
        {
          label: "Iframe Key Generation",
          icon: <KeyOutline />,
          click: () => setOpenIframeKeyGeneration(),
        },
        {
          label: "Access Matrix",
          icon: <PasswordOutlined />,
          to: getRegister(URL_MERCHANT_SERVICE_ACCESS),
        },
        {
          label: "IP Whitelist",
          icon: <WbIridescent />,
          to: getRegister(URL_MERCHANT_IP_WHITELIST),
        },
      ],
    },
    {
      label: "Agent Management",
      icon: <Diversity2Outlined />,
      visible: itemExists(permissions, [CAN_ADMIN]),
      children: [
        {
          label: "Internal User charges",
          icon: <CreditCardOutlined />,
          to: getRegister(URL_YOYA_CHARGES),
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
      ],
    },

    // ── PAYMENTS ──────────────────────────────────────────────────
    { _section: "Payments" },
    {
      label: "Merchant Code Payments",
      icon: <StorefrontOutlined />,
      to: getRegister(URL_MERCHANT_CODES),
      visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
      children: [
        {
          label: "Merchant Codes",
          icon: <StorefrontOutlined />,
          to: getRegister(URL_MERCHANT_CODES),
        },
        {
          label: "Merchant Codes Payments",
          icon: <WalletOutline />,
          to: getRegister(URL_MERCHANT_PAYMENTS),
        },
      ],
    },
    {
      label: "QR Code Payments",
      icon: <QrCode2Outlined />,
      visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
      children: [
        {
          label: "Static Merchant QR Code",
          icon: <QrCodeScannerOutlined />,
          click: () => setOpenStaticQRCode(),
        },
        {
          label: "Dynamic Item QR Code",
          icon: <QrCodeScannerOutlined />,
          to: getRegister(URL_DYNAMIC_QR_CODE),
        },
        {
          label: "QR Code Payments",
          icon: <QrCodeScannerOutlined />,
          to: getRegister(URL_QR_CODE_PAYMENT),
        },
      ],
    },
    {
      label: "Iframe Payments",
      icon: <Web />,
      to: getRegister(URL_IFRAME_PAYMENTS),
      visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
    },

    // ── FINANCE ───────────────────────────────────────────────────
    { _section: "Finance" },
    {
      label: "Transactions & Reconciliations",
      icon: <WalletOutline />,
      visible: itemExists(permissions, [CAN_ADMIN]),
      children: [
        {
          label: "Reconciliation Report",
          icon: <AssessmentOutlined />,
          to: getRegister(URL_RECONCILIATIONS_REPORTS),
        },
        {
          label: "Transaction Monitoring",
          icon: <CastOutlined />,
          to: getRegister(URL_TRANSACTION_MONITORING),
        },
        {
          label: "Reconciliation Statements",
          icon: <AssignmentOutlined />,
          to: getRegister(URL_RECONCILIATION_STATEMENTS),
        },
        {
          label: "Partner Item Mapping",
          icon: <ThreePOutlined />,
          to: getRegister(URL_PARTNER_ITEM_MAPPING),
        },
        {
          label: "Refunds",
          icon: <SettingsBackupRestoreOutlined />,
          to: getRegister(URL_REVERSE_TRANSACTIONS),
        },
        {
          label: "Mark Transaction",
          icon: <BookmarkAddOutlined />,
          to: getRegister(URL_MARK_TRANSACTION),
        },
      ],
    },
    {
      label: "Revenue & Settlements",
      icon: <DatasetLinkedOutlined />,
      children: [
        {
          label: "Replenishment",
          icon: <RedeemOutlined />,
          to: getRegister(URL_REPLENISHMENT),
        },
        {
          label: "Credit Holdings",
          icon: <CreditScoreOutlined />,
          to: getRegister(URL_CREDIT_HOLDINGS),
        },
        {
          label: "Liquidations",
          icon: <RedeemOutlined />,
          to: getRegister(URL_LIQUIDATIONS),
        },
        {
          label: "Debit Holdings",
          icon: <CreditScoreOutlined />,
          to: getRegister(URL_DEBIT_HOLDINGS),
        },
        {
          label: "Monitored Accounts",
          icon: <TvOutlined />,
          to: getRegister(URL_MONITORED_ACCOUNTS),
        },
        {
          label: "Account Alerts",
          icon: <NotificationsActiveOutlined />,
          to: getRegister(URL_ACCOUNT_ALERTS),
        },
        {
          label: "Credit Holding Types",
          icon: <CreditScoreOutlined />,
          to: getRegister(URL_CREDIT_HOLDING_TYPES),
        },
        {
          label: "Debit Holding Types",
          icon: <CreditScoreOutlined />,
          to: getRegister(URL_DEBIT_HOLDINGS_TYPES),
        },
        {
          label: "Liquidation Bank Codes",
          icon: <AccountBalanceOutlined />,
          to: getRegister(URL_LIQUIDATION_BANK_CODES),
        },
        {
          label: "Liquidation Bank Accounts",
          icon: <AccountBalanceOutlined />,
          to: getRegister(URL_BANK_ACCOUNTS),
        },
      ],
    },
    {
      label: "Accounting",
      icon: <AccountTreeOutlined />,
      visible: itemExists(permissions, [CAN_ADMIN]),
      children: [
        {
          label: "Chart of Accounts",
          icon: <StackedBarChartOutlined />,
          to: getRegister(URL_CHARTS_CONFIGURABLES),
        },
        {
          label: "Payment Items",
          icon: <CategoryOutlined />,
          to: getRegister(URL_PAYMENT_ITEMS),
        },
        {
          label: "Payment Types",
          icon: <CategoryOutlined />,
          to: getRegister(URL_PAYMENT_TYPES),
        },
        {
          label: "Charge Assignment",
          icon: <CashOutline />,
          to: getRegister(URL_CHARGE_ASSIGNMENT),
        },
        {
          label: "Partners",
          icon: <PeopleAltOutlined />,
          to: getRegister(URL_PARTNERS),
        },
        {
          label: "Partners charges",
          icon: <PeopleAltOutlined />,
          to: getRegister(URL_PARTNER_CHARGES),
        },
        {
          label: "System Wallets",
          icon: <PeopleAltOutlined />,
          to: getRegister(URL_SYSTEM_WALLETS),
        },
      ],
    },
    {
      label: "Finance & Treasury",
      icon: <MoneyOffOutlined />,
      visible: itemExists(permissions, [CAN_ADMIN]),
      children: [
        {
          label: "Revenue Sharing",
          icon: <ShareOutlined />,
          children: [
            {
              label: "Float Interest Statements",
              icon: <SummarizeOutlined />,
              to: getRegister(URL_FLOAT_INTEREST_STATEMENTS),
            },
            {
              label: "Float Interest Configs",
              icon: <SettingsSuggestOutlined />,
              to: getRegister(URL_FLOAT_INTEREST_CONFIGS),
            },
          ],
        },
        {
          label: "Settlement Engine",
          icon: <EngineeringOutlined />,
          children: [
            {
              label: "Eligibility Review",
              icon: <NotListedLocationOutlined />,
              click: () => setOpenSettlementEligibilityReview(),
            },
            {
              label: "Batches",
              icon: <BatchPredictionOutlined />,
              children: [
                {
                  label: "All Batches",
                  icon: <BatchPredictionOutlined />,
                  to: getRegister(URL_SETTLEMENT_BATCHES),
                },
                {
                  label: "Draft",
                  icon: <SaveAsOutlined />,
                  to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${DRAFT}?status=${DRAFT}`,
                },
                {
                  label: "Pending Approval",
                  icon: <PendingOutlined />,
                  to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${PENDING_APPROVAL}?status=${PENDING_APPROVAL}`,
                },
                {
                  label: "Approved",
                  icon: <VerifiedOutlined />,
                  to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${APPROVED}?status=${APPROVED}`,
                },
                {
                  label: "Sent to Bank",
                  icon: <SendOutlined />,
                  to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${SENT_TO_BANK}?status=${SENT_TO_BANK}`,
                },
                {
                  label: "Settled",
                  icon: <CheckCircleOutline />,
                  to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${SETTLED}?status=${SETTLED}`,
                },
                {
                  label: "Failed / Retry",
                  icon: <ErrorOutline />,
                  to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${FAILED}?status=${FAILED}`,
                },
              ],
            },
            {
              label: "Approvals",
              icon: <ApprovalOutlined />,
              children: [
                {
                  label: "My Queue",
                  icon: <QueueOutlined />,
                  to: getRegister(URL_SETTLEMENT_APPROVALS),
                },
                {
                  label: "Level 1 Treasury",
                  icon: <ApprovalOutlined />,
                  to: `${getRegister(URL_SETTLEMENT_APPROVALS)}?level=1`,
                },
                {
                  label: "Level 2 Compliance",
                  icon: <ApprovalOutlined />,
                  to: `${getRegister(URL_SETTLEMENT_APPROVALS)}?level=2`,
                },
                {
                  label: "Level 3 Finance Manager",
                  icon: <ApprovalOutlined />,
                  to: `${getRegister(URL_SETTLEMENT_APPROVALS)}?level=3`,
                },
              ],
            },
            {
              label: "Settlement Reports",
              icon: <DescriptionOutlined />,
              children: [
                {
                  label: "Daily Settlement Report",
                  icon: <DescriptionOutlined />,
                  to: "/reports/daily-batch-settlement-report",
                },
                {
                  label: "Compliance Report",
                  icon: <DescriptionOutlined />,
                  to: "/reports/compliance-report",
                },
              ],
            },
            {
              label: "Configurations",
              icon: <SettingsSuggestOutlined />,
              children: [
                {
                  label: "Beneficiaries",
                  icon: <PeopleAltOutlined />,
                  to: getRegister(URL_SETTLEMENT_BENEFICIARIES),
                },
                {
                  label: "Rules",
                  icon: <WorkHistoryOutlined />,
                  to: getRegister(URL_SETTLEMENT_RULES),
                },
              ],
            },
          ],
        },
      ],
    },

    // ── OPERATIONS ────────────────────────────────────────────────
    { _section: "Operations" },
    {
      label: "Fraud Detection",
      icon: <ReportOutlined />,
      visible: itemExists(permissions, [CAN_ADMIN]),
      dot: true,
      dotColor: "#f97316",
      children: [
        {
          label: "Fraud Dashboard",
          icon: <GridOutline />,
          to: URL_FRAUD_DASHBOARD,
        },
        {
          label: "Fraud Alerts",
          icon: <BrowseGalleryOutlined />,
          to: getRegister(URL_FRAUD_ALERTS),
        },
        {
          label: "Fraud Cases",
          icon: <CasesOutlined />,
          to: getRegister(URL_FRAUD_CASES),
        },
        {
          label: "Fraud Weight Score",
          icon: <Scale />,
          to: getRegister(URL_FRAUD_WEIGHTSCORE),
        },
        {
          label: "Fraud Audit Logs",
          icon: <ArticleOutlined />,
          to: getRegister(URL_FRAUD_AUDIT_LOGS),
        },
        {
          label: "Fraud Rule History",
          icon: <WorkHistoryOutlined />,
          to: getRegister(URL_FRAUD_RULES_HISTORY),
        },
        {
          label: "Fraud Events",
          icon: <EventOutlined />,
          to: getRegister(URL_FRAUD_EVENTS),
        },
        {
          label: "Fraud Templates",
          icon: <EventOutlined />,
          to: getRegister(URL_FRAUD_TEMPLATES),
        },
        {
          label: "Fraud Configurations",
          icon: <SettingsInputComponentOutlined />,
          to: getRegister(URL_FRAUD_CONFIGURATIONS),
        },
      ],
    },
    {
      label: "Wallet Transactions",
      icon: <WalletOutline />,
      children: [
        {
          label: "Top Up Wallet",
          icon: <Add />,
          to: "/wallet-deposit/create",
          visible: itemExists(permissions, [CAN_AGENT, CAN_MERCHANT]),
        },
        {
          label: "Transfer",
          icon: <NearMeOutlined />,
          to: "/wallet-transfer/create",
          visible: itemExists(permissions, [CAN_AGENT, CAN_MERCHANT]),
        },
        {
          label: "Transactions",
          icon: <SaveOutline />,
          to: URL_WALLET_TRANSFERS,
        },
      ],
    },
    {
      label: "Utilities",
      icon: <LocalMallOutlined />,
      children: [
        {
          label: "Make Utility Payment",
          icon: <RequestQuoteOutlined />,
          to: "/utility-payments/create",
        },
        {
          label: "Utility Payments",
          icon: <ListAltOutlined />,
          to: getRegister(URL_UTILITY_TRANSACTIONS),
        },
      ],
    },
    {
      label: "E-Money Ledgers",
      icon: <MoneyOutlined />,
      visible: itemExists(permissions, [CAN_ADMIN]),
      children: [
        {
          label: "General Ledger",
          icon: <DocumentTextOutline />,
          to: URL_LEDGERS,
        },
        {
          label: "Merchant Sub Ledger",
          icon: <DocumentTextOutline />,
          to: "/reports/merchant-sub-ledgers",
        },
        {
          label: "Agent Sub Ledger",
          icon: <DocumentTextOutline />,
          to: "/reports/agent-sub-ledgers",
        },
        {
          label: "Public User Sub Ledger",
          icon: <DocumentTextOutline />,
          to: "/reports/public-user-sub-ledgers",
        },
        {
          label: "Financial Sub Ledger",
          icon: <DocumentTextOutline />,
          to: "/reports/financial-sub-ledgers",
        },
      ],
    },
    {
      label: "Reports",
      icon: <LayersOutline />,
      children: [
        {
          label: "Adhoc Reports",
          icon: <DocumentTextOutline />,
          to: "/reports/adhoc",
          visible: itemExists(permissions, [
            CAN_ADMIN,
            CAN_AGENT,
            CAN_MERCHANT,
          ]),
        },
        {
          label: "Chart of Accounts",
          icon: <DocumentTextOutline />,
          to: "/reports/chart-of-accounts-report",
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "Transactions",
          icon: <DocumentTextOutline />,
          to: "/reports/transactions",
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "Sales Summary",
          icon: <PaidOutlined />,
          visible: itemExists(permissions, [
            CAN_ADMIN,
            CAN_AGENT,
            CAN_MERCHANT,
          ]),
          children: [
            {
              label: "Transaction Summary",
              icon: <DocumentTextOutline />,
              to: URL_TRANSACTION_SUMMARY,
            },
            {
              label: "Merchant Summary",
              icon: <DocumentTextOutline />,
              to: URL_MERCHANT_SUMMARY,
            },
            {
              label: "Payment Item Summary",
              icon: <DocumentTextOutline />,
              to: URL_PAYMENT_ITEM_SUMMARY,
            },
          ],
        },
        {
          label: "Commissions Summary",
          icon: <PaymentsTwoTone />,
          visible: itemExists(permissions, [
            CAN_ADMIN,
            CAN_AGENT,
            CAN_MERCHANT,
          ]),
          children: [
            {
              label: "Partner Commission Summary",
              icon: <DocumentTextOutline />,
              to: URL_PARTNER_COMMISSION_SUMMARY,
            },
            {
              label: "Agent Commission Summary",
              icon: <DocumentTextOutline />,
              to: URL_AGENT_COMMISSION_SUMMARY,
            },
            {
              label: "Yoya Commission Summary",
              icon: <DocumentTextOutline />,
              to: URL_YOYA_COMMISSION_SUMMARY,
            },
          ],
        },
        {
          label: "Channel Separations",
          icon: <CallSplit />,
          visible: itemExists(permissions, [
            CAN_ADMIN,
            CAN_AGENT,
            CAN_MERCHANT,
          ]),
          children: [
            {
              label: "Transaction Channel Separation",
              icon: <CurrencyExchange />,
              to: URL_TRANSACTION_CHANNEL_SEPARATION,
            },
            {
              label: "Payment Item Channel Report",
              icon: <CurrencyExchange />,
              to: URL_PAYMENT_ITEM_CHANNEL_REPORT,
            },
          ],
        },
        {
          label: "General Ledger",
          icon: <DocumentTextOutline />,
          to: "/reports/general-ledger-report",
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "Profit and Loss",
          icon: <DocumentTextOutline />,
          to: "/reports/profit-and-loss-report",
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "Trial Balance",
          icon: <DocumentTextOutline />,
          to: getRegister(URL_TRIAL_BALANCE),
          visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
        },
        {
          label: "Incident Ticket Categories",
          icon: <DocumentTextOutline />,
          to: getRegister(URL_INCIDENT_TICKET_CATEGORY),
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "Incident Ticket Severity",
          icon: <DocumentTextOutline />,
          to: getRegister(URL_INCIDENT_TICKET_SEVERITY),
          visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
        },
        // {
        //   label: "Wallet Balance",
        //   icon: <DocumentTextOutline />,
        //   to: "/reports/wallet-balance-report",
        //   visible: itemExists(permissions, [
        //     CAN_ADMIN,
        //     CAN_AGENT,
        //     CAN_MERCHANT,
        //   ]),
        // },
      ],
    },
    {
      label: "System Reports",
      icon: <PeopleOutline />,
      visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT, CAN_AGENT]),
      children: [
        {
          label: "Utility Logs",
          icon: <ListSharp />,
          to: `/reports/utility-logs-report`,
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "External Call Logs",
          icon: <LinearScale />,
          to: `/reports/external-call-logs-report`,
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "Liquidation Logs",
          icon: <RedeemOutlined />,
          to: `/reports/liquidation-logs`,
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "System Exceptions",
          icon: <SettingsSystemDaydream />,
          to: `/reports/system-exceptions-report`,
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "Customer Report Requests",
          icon: <PeopleOutline />,
          to: `/reports/customer-report-requests`,
          visible: itemExists(permissions, [
            CAN_ADMIN,
            CAN_MERCHANT,
            CAN_AGENT,
          ]),
        },
        {
          label: "Ticket Category Report",
          icon: <PeopleOutline />,
          to: URL_INCIDENT_TICKET_CATEGORY_REPORT,
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
        {
          label: "Ticket Severity Report",
          icon: <PeopleOutline />,
          to: URL_INCIDENT_TICKET_SEVERITY_REPORT,
          visible: itemExists(permissions, [CAN_ADMIN]),
        },
      ],
    },
    {
      label: "App Menu Management",
      icon: <PaymentOutlined />,
      visible: itemExists(permissions, [CAN_ADMIN]),
      to: getRegister(URL_PAYMENT_SERVICES),
    },
    {
      label: "Audit Activity",
      icon: <WorkHistoryOutlined />,
      visible: itemExists(permissions, [CAN_ADMIN]) || is_staff,
      children: [
        {
          label: "Security Activities",
          visible: itemExists(permissions, [CAN_ADMIN]),
          to: getRegister(URL_SECURITY_ACTIVITY),
          icon: <SecurityOutlined />,
        },
        {
          label: "Business Audit Logs",
          visible: itemExists(permissions, [CAN_ADMIN]),
          to: getRegister(URL_BUSINESS_AUDIT_LOGS),
          icon: <SecurityOutlined />,
        },
      ],
    },
    {
      label: "Support",
      icon: <HelpOutline />,
      children: [
        {
          label: "Enquiries",
          icon: <ConfirmationNumberOutlined />,
          to: getRegister(URL_CONTACT_US),
        },
        {
          label: "Incident Ticketing",
          icon: <BugReportOutlined />,
          children: [
            {
              label: "Create Ticket",
              icon: <Add />,
              to: "/incident_ticketing/create",
            },
            {
              label: "Ticket List",
              icon: <ListAltOutlined />,
              to: getRegister(URL_INCIDENT_TICKETING),
              visible: itemExists(permissions, [CAN_ADMIN]),
            },
            {
              label: "Raised Tickets",
              icon: <ListAltOutlined />,
              to: getRegister(URL_RAISED_TICKETS),
            },
            {
              label: "Assigned Tickets",
              icon: <ListAltOutlined />,
              to: getRegister(URL_ASSIGNED_TICKETS),
            },
          ],
        },
        {
          label: "Ticket Categorization",
          icon: <CategoryOutlined />,
          children: [
            {
              label: "Create Category",
              icon: <Add />,
              to: "/ticket_categorization/create",
            },
            {
              label: "Ticket Categories",
              icon: <ListAltOutlined />,
              to: getRegister(URL_TICKET_CATEGORIZATION),
            },
          ],
        },
      ],
    },
    {
      icon: <SettingsOutline />,
      label: "Settings",
      visible: true,
      to: "/settings",
    },
  ];
  return menu;
}

/* ─────────────────────────────────────────────────────────────────
   Key / path helpers (unchanged)
   ───────────────────────────────────────────────────────────────── */
const findParentKeys = (
  menu: any[],
  targetPath: string,
  parentKeys: string[] = [],
): string[] => {
  const norm = (p: string) => p?.split("?")[0] ?? "";
  const normTarget = norm(targetPath);
  for (const item of menu) {
    if (item._section) continue;
    const itemKey =
      item.key || item.label?.toLowerCase().replace(/\s+/g, "-") || item.to;
    if (norm(item.to) === normTarget) return parentKeys;
    if (item.children?.length) {
      const found = findParentKeys(item.children, targetPath, [
        ...parentKeys,
        itemKey,
      ]);
      if (found.length > 0) return found;
      if (item.children.some((c: any) => norm(c.to) === normTarget))
        return [...parentKeys, itemKey];
    }
  }
  return [];
};

/* ─────────────────────────────────────────────────────────────────
   LeftMenu component
   ───────────────────────────────────────────────────────────────── */
export const LeftMenu: React.FC = () => {
  const { data } = useGlobalContext();
  const settingsItems = useSettings();
  const leftMenuItems = LeftMenuItems();
  const location = useLocation();
  const { user }: any = useAuth();

  const menuSource = data?.showSettings ? settingsItems : leftMenuItems;

  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>(["default"]);

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const normPath = currentPath.split("?")[0];
    const parentKeys = findParentKeys(menuSource, currentPath);
    const flat = flattenMenu(menuSource);
    const cur =
      flat.find((i: any) => i.to === currentPath) ??
      flat.find((i: any) => i.to?.split("?")[0] === normPath);
    const curKey =
      cur?.key || cur?.label?.toLowerCase().replace(/\s+/g, "-") || currentPath;
    setOpenKeys((prev) =>
      JSON.stringify(prev) === JSON.stringify(parentKeys) ? prev : parentKeys,
    );
    setSelectedKeys((prev) => {
      const n = [curKey || "default"];
      return JSON.stringify(prev) === JSON.stringify(n) ? prev : n;
    });
  }, [location.pathname, location.search]);

  const flat = flattenMenu(menuSource);
  useDynamicPageTitle(flat);

  // Split into section groups for rendering
  const sections: { label: string; items: any[] }[] = [];
  let current: { label: string; items: any[] } = { label: "", items: [] };
  for (const item of menuSource) {
    if (item._section) {
      if (current.items.length) sections.push(current);
      current = { label: item._section, items: [] };
    } else {
      current.items.push(item);
    }
  }
  if (current.items.length) sections.push(current);

  // User initials
  const name = user?.full_name ?? user?.username ?? "System Admin";
  const role = user?.role_display ?? "Administrator";
  const initials = name
    .split(" ")
    .map((w: string) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <SidebarShell>
      {/* Logo */}
      <LogoArea>
        <Link
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            textDecoration: "none",
          }}
        >
          <LogoBadge>1x</LogoBadge>
          <LogoText>
            <span style={{ color: "#fff" }}>One</span>
            <span style={{ color: "#7ab3e8" }}>PayX</span>
          </LogoText>
        </Link>
      </LogoArea>

      {/* Scrollable menu */}
      <MenuScroll>
        {sections.map((section, si) => (
          <div key={si}>
            {section.label && <SectionLabel>{section.label}</SectionLabel>}
            <Menu
              className="font13 leftmenu"
              mode="inline"
              openKeys={openKeys}
              selectedKeys={selectedKeys}
              onOpenChange={setOpenKeys}
              theme="light"
              style={{ background: "transparent", border: "none" }}
            >
              {RecursiveMenuWithDots(section.items)}
            </Menu>
          </div>
        ))}
      </MenuScroll>

      {/* User profile footer */}
      <UserArea>
        <Avatar>{initials}</Avatar>
        <UserInfo>
          <UserName>{name}</UserName>
          <UserRole>{role}</UserRole>
        </UserInfo>
        <OnlineDot />
      </UserArea>
    </SidebarShell>
  );
};

/* ─────────────────────────────────────────────────────────────────
   RecursiveMenuWithDots — same as RecursiveMenu but supports dot badge
   ───────────────────────────────────────────────────────────────── */
import React from "react";
import { Menu as AntMenu } from "antd";

const { SubMenu, Item } = AntMenu;

function RecursiveMenuWithDots(data: any[]): any {
  return data.map(
    ({
      key,
      label,
      icon,
      to,
      click,
      visible,
      children = [],
      hidden = false,
      dot,
      dotColor,
    }) => {
      if (hidden) return null;
      const itemKey =
        key ||
        label?.toLowerCase().replace(/\s+/g, "-") ||
        to ||
        Math.random().toString();

      const labelNode = dot ? (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <span>{label}</span>
          <Dot $color={dotColor} />
        </span>
      ) : (
        label
      );

      const show = visible == null || visible;
      if (!show) return null;

      if (!children?.length) {
        return (
          <Item key={itemKey} icon={icon}>
            {to ? (
              <Link to={to}>{labelNode}</Link>
            ) : click ? (
              <div onClick={click}>{labelNode}</div>
            ) : (
              <div>{labelNode}</div>
            )}
          </Item>
        );
      }

      return (
        <SubMenu key={itemKey} title={labelNode} icon={icon}>
          {RecursiveMenuWithDots(children)}
        </SubMenu>
      );
    },
  );
}

// import { Menu } from "antd";
// import { RecursiveMenu } from "../menu/RecursiveMenu";
// import { useState, useEffect } from "react";
// import { useLocation } from "react-router-dom";
// import { useSettings } from "../../constants/settings";
// import { useGlobalContext } from "../../context/GlobalContext";
// import {
//   CashOutline,
//   DocumentTextOutline,
//   GridOutline,
//   KeyOutline,
//   LayersOutline,
//   SaveOutline,
//   SettingsOutline,
//   ShieldCheckmarkOutline,
//   WalletOutline,
// } from "@raresail/react-ionicons";
// import {
//   URL_ACCOUNT_ALERTS,
//   URL_AGENT_COMMISSION_SUMMARY,
//   URL_AGENTS,
//   URL_APPLICATION_USERS,
//   URL_AUDIT_ACTIVITY,
//   URL_BANK_ACCOUNTS,
//   URL_CHARGE_ASSIGNMENT,
//   URL_CHARTS_CONFIGURABLES,
//   URL_CONTACT_US,
//   URL_CREDIT_HOLDING_TYPES,
//   URL_CREDIT_HOLDINGS,
//   URL_DEBIT_HOLDINGS,
//   // URL_CREDIT_HOLDINGS,
//   URL_DEBIT_HOLDINGS_TYPES,
//   URL_DYNAMIC_QR_CODE,
//   URL_FRAUD_ALERTS,
//   URL_FRAUD_AUDIT_LOGS,
//   URL_FRAUD_CASES,
//   URL_FRAUD_CONFIGURATIONS,
//   URL_FRAUD_DASHBOARD,
//   URL_FRAUD_EVENTS,
//   // URL_FRAUD_RULES,
//   URL_FRAUD_RULES_HISTORY,
//   URL_FRAUD_TEMPLATES,
//   URL_FRAUD_WEIGHTSCORE,
//   URL_IFRAME_PAYMENTS,
//   URL_INTERNAL_MERCHANT_ACCESS,
//   URL_PARTNER_ITEM_MAPPING,
//   URL_LEDGERS,
//   URL_LIQUIDATION_BANK_CODES,
//   URL_LIQUIDATIONS,
//   URL_MARK_TRANSACTION,
//   URL_MERCHANT_CODES,
//   URL_MERCHANT_FEES,
//   URL_MERCHANT_IP_WHITELIST,
//   // URL_MERCHANT_MONTHLY_REPORT,
//   URL_MERCHANT_PAYMENTS,
//   URL_MERCHANT_SERVICE_ACCESS,
//   // URL_MERCHANT_SUB_LEDGERS,
//   URL_MERCHANT_USERS,
//   URL_MERCHANTS,
//   URL_MONITORED_ACCOUNTS,
//   URL_PARTNER_CHARGES,
//   URL_PARTNER_COMMISSION_SUMMARY,
//   URL_PARTNERS,
//   // URL_PAYMENT_ITEM_MONTHLY_REPORT,
//   URL_PAYMENT_ITEMS,
//   URL_PAYMENT_SERVICES,
//   URL_PAYMENT_TYPES,
//   URL_PUBLIC_USERS,
//   URL_QR_CODE_PAYMENT,
//   URL_RECONCILIATION_STATEMENTS,
//   URL_RECONCILIATIONS_REPORTS,
//   URL_REJECTED_APPLICATION_USERS,
//   URL_REPLENISHMENT,
//   URL_REVERSE_TRANSACTIONS,
//   URL_STAFF_USERS,
//   URL_SYSTEM_WALLETS,
//   URL_TRANSACTION_MONITORING,
//   URL_TRIAL_BALANCE,
//   URL_USERS,
//   URL_WALLET_TRANSFERS,
//   URL_YOYA_CHARGES,
//   URL_YOYA_COMMISSION_SUMMARY,
//   URL_UTILITY_TRANSACTIONS,
//   URL_TRANSACTION_CHANNEL_SEPARATION,
//   URL_PAYMENT_ITEM_CHANNEL_REPORT,
//   URL_FLOAT_INTEREST_CONFIGS,
//   URL_FLOAT_INTEREST_STATEMENTS,
//   URL_TRANSACTION_SUMMARY,
//   URL_SETTLEMENT_BATCHES,
//   URL_SETTLEMENT_RULES,
//   URL_SETTLEMENT_BENEFICIARIES,
//   URL_SETTLEMENT_APPROVALS,
//   URL_SECURITY_ACTIVITY,
//   URL_BUSINESS_AUDIT_LOGS,
//   URL_MERCHANT_SUMMARY,
//   URL_PAYMENT_ITEM_SUMMARY,
//   URL_INCIDENT_TICKETING,
//   URL_TICKET_CATEGORIZATION,
//   URL_RAISED_TICKETS,
//   URL_ASSIGNED_TICKETS,
// } from "../../utils/urls";
// import {
//   getForm,
//   getRegister,
//   itemExists,
//   useGetUserPermissions,
// } from "../../utils/helpers";
// import {
//   APPROVED,
//   CAN_ADMIN,
//   CAN_AGENT,
//   CAN_CREATE_FEES,
//   CAN_MERCHANT,
//   DRAFT,
//   PENDING_APPROVAL,
//   SENT_TO_BANK,
//   SETTLED,
//   FAILED,
//   // REJECTED,
// } from "../../utils/constants";
// import {
//   Add,
//   AssessmentOutlined,
//   BusinessOutlined,
//   CastOutlined,
//   // DashboardCustomizeOutlined,
//   DatasetLinkedOutlined,
//   Diversity1Outlined,
//   Diversity2Outlined,
//   Group,
//   NearMeOutlined,
//   // PanoramaHorizontalOutlined,
//   PasswordOutlined,
//   PeopleOutline,
//   // TimelineOutlined,
//   SettingsBackupRestoreOutlined,
//   ThreePOutlined,
//   // UpcomingOutlined,
//   // AssuredWorkloadOutlined,
//   AccountTreeOutlined,
//   StackedBarChartOutlined,
//   CategoryOutlined,
//   PeopleAltOutlined,
//   // LocalPoliceOutlined,
//   WbIridescent,
//   BookmarkAddOutlined,
//   // ConfirmationNumberOutlined,
//   CreditCardOutlined,
//   RedeemOutlined,
//   PaymentOutlined,
//   ListSharp,
//   LinearScale,
//   SettingsSystemDaydream,
//   AssignmentOutlined,
//   QrCode2Outlined,
//   QrCodeScannerOutlined,
//   MoneyOffOutlined,
//   // GavelOutlined,
//   BrowseGalleryOutlined,
//   CasesOutlined,
//   ArticleOutlined,
//   WorkHistoryOutlined,
//   EventOutlined,
//   SensorOccupiedOutlined,
//   SupportAgentOutlined,
//   StorefrontOutlined,
//   Scale,
//   SettingsInputComponentOutlined,
//   PersonOff,
//   Web,
//   PaidOutlined,
//   ConfirmationNumberOutlined,
//   // AssignmentTurnedIn,
//   MoneyOutlined,
//   TvOutlined,
//   NotificationsActiveOutlined,
//   CreditScoreOutlined,
//   AccountBalanceOutlined,
//   PaymentsTwoTone,
//   LocalMallOutlined,
//   RequestQuoteOutlined,
//   ListAltOutlined,
//   // PriceCheckOutlined,
//   CallSplit,
//   CurrencyExchange,
//   ShareOutlined,
//   ReportOutlined,
//   EngineeringOutlined,
//   NotListedLocationOutlined,
//   BatchPredictionOutlined,
//   SaveAsOutlined,
//   PendingOutlined,
//   VerifiedOutlined,
//   SendOutlined,
//   CheckCircleOutline,
//   ErrorOutline,
//   ApprovalOutlined,
//   QueueOutlined,
//   SettingsSuggestOutlined,
//   DescriptionOutlined,
//   SummarizeOutlined,
//   SecurityOutlined,
//   // NotInterestedOutlined,
//   // Support and ticketing icons
//   HelpOutline,
//   BugReportOutlined,
// } from "@mui/icons-material";
// import { useModals } from "../common/useModals";
// import { flattenMenu } from "../../utils/menuUtils";
// import { useDynamicPageTitle } from "../../hooks/dynamic_title/useDynamicPageTitle";
// import { useAuth } from "../../context/AuthContext";

// function LeftMenuItems(): any {
//   const permissions = useGetUserPermissions();
//   const { is_staff }: any = useAuth();
//   const {
//     setOpenGenerateApiKey,
//     setOpenStaticQRCode,
//     setOpenIframeKeyGeneration,
//     setOpenSettlementEligibilityReview,
//   } = useModals();

//   const menu: any = [
//     {
//       label: "Dashboard",
//       to: "/",
//       visible: true,
//       key: "default",
//       icon: <GridOutline />,
//     },
//     {
//       label: "User Management",
//       icon: <PeopleOutline />,
//       to: getRegister(URL_USERS),
//       visible: itemExists(permissions, [CAN_ADMIN]),
//       children: [
//         {
//           label: "Add User",
//           icon: <Add />,
//           to: getForm(URL_USERS),
//         },
//         {
//           label: "Pending Applications",
//           icon: <SensorOccupiedOutlined />,
//           to: getRegister(URL_APPLICATION_USERS),
//         },
//         {
//           label: "Rejected Applications",
//           icon: <PersonOff />,
//           to: getRegister(URL_REJECTED_APPLICATION_USERS),
//         },
//         {
//           label: "Users",
//           icon: <Group />,
//           // to: getRegister(URL_USERS),
//           children: [
//             {
//               label: "All Users",
//               icon: <Group />,
//               to: getRegister(URL_USERS),
//             },
//             {
//               label: "Staffs",
//               icon: <Group />,
//               to: getRegister(URL_STAFF_USERS),
//             },
//             {
//               label: "Agents",
//               icon: <SupportAgentOutlined />,
//               to: getRegister(URL_AGENTS),
//             },
//             {
//               label: "Merchants",
//               icon: <StorefrontOutlined />,
//               to: getRegister(URL_MERCHANTS),
//             },
//             {
//               label: "Public Users",
//               icon: <Group />,
//               to: getRegister(URL_PUBLIC_USERS),
//             },
//           ],
//         },
//         {
//           label: "Internal Merchant Access",
//           icon: <PasswordOutlined />,
//           to: getRegister(URL_INTERNAL_MERCHANT_ACCESS),
//         },
//         {
//           label: "User Audit Trail",
//           icon: <ShieldCheckmarkOutline />,
//           to: getRegister(URL_AUDIT_ACTIVITY),
//         },
//       ],
//     },
//     {
//       label: "Merchant Management",
//       icon: <Diversity1Outlined />,
//       visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
//       children: [
//         // {
//         //   label: "Add Merchant User",
//         //   icon: <Add />,
//         //   to: getForm(URL_MERCHANT_USERS),
//         // },
//         {
//           label: "Merchant Users",
//           icon: <Group />,
//           to: getRegister(URL_MERCHANT_USERS),
//         },
//         {
//           label: "Merchant Fees",
//           icon: <BusinessOutlined />,
//           to: getRegister(URL_MERCHANT_FEES),
//           visible: itemExists(permissions, [CAN_CREATE_FEES]),
//         },
//         {
//           label: "Encryption Key Generation",
//           icon: <KeyOutline />,
//           click: () => {
//             setOpenGenerateApiKey();
//           },
//         },
//         {
//           label: "Iframe Key Generation",
//           icon: <KeyOutline />,
//           click: () => {
//             setOpenIframeKeyGeneration();
//           },
//         },
//         {
//           label: "Access Matrix",
//           icon: <PasswordOutlined />,
//           to: getRegister(URL_MERCHANT_SERVICE_ACCESS),
//         },
//         {
//           label: "IP Whitelist",
//           icon: <WbIridescent />,
//           to: getRegister(URL_MERCHANT_IP_WHITELIST),
//         },
//       ],
//     },
//     {
//       label: "Merchant Codes Payments",
//       icon: <StorefrontOutlined />,
//       to: getRegister(URL_MERCHANT_CODES),
//       visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
//       children: [
//         {
//           label: "Merchant Codes",
//           icon: <StorefrontOutlined />,
//           to: getRegister(URL_MERCHANT_CODES),
//         },
//         {
//           label: "Merchant Codes Payments",
//           icon: <WalletOutline />,
//           to: getRegister(URL_MERCHANT_PAYMENTS),
//         },
//       ],
//     },
//     {
//       label: "Agent Management",
//       icon: <Diversity2Outlined />,
//       visible: itemExists(permissions, [CAN_ADMIN]),
//       children: [
//         // {
//         //   label: "Agent Dashboard",
//         //   icon: <DashboardCustomizeOutlined />,
//         //   to: "",
//         // },
//         // {
//         //   label: "Encryption Keys",
//         //   icon: <KeyOutline />,
//         //   to: "",
//         // },
//         // {
//         //   label: "Access Matrix",
//         //   icon: <PasswordOutlined />,
//         //   to: "",
//         // },
//         {
//           label: "Internal User charges",
//           icon: <CreditCardOutlined />,
//           to: getRegister(URL_YOYA_CHARGES),
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//       ],
//     },
//     {
//       label: "QR Code Payments",
//       icon: <QrCode2Outlined />,
//       visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
//       children: [
//         {
//           label: "Static Merchant QR Code",
//           icon: <QrCodeScannerOutlined />,
//           click: () => {
//             setOpenStaticQRCode();
//           },
//         },
//         {
//           label: "Dynamic Item QR Code",
//           icon: <QrCodeScannerOutlined />,
//           to: getRegister(URL_DYNAMIC_QR_CODE),
//         },
//         {
//           label: "QR Code Payments",
//           icon: <QrCodeScannerOutlined />,
//           to: getRegister(URL_QR_CODE_PAYMENT),
//         },
//       ],
//     },
//     {
//       label: "Iframe Payments",
//       icon: <Web />,
//       to: getRegister(URL_IFRAME_PAYMENTS),
//       visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
//     },
//     {
//       label: "Transactions & Reconciliations",
//       icon: <WalletOutline />,
//       visible: itemExists(permissions, [CAN_ADMIN]),
//       children: [
//         {
//           label: "Reconciliation Report",
//           icon: <AssessmentOutlined />,
//           to: getRegister(URL_RECONCILIATIONS_REPORTS),
//         },
//         {
//           label: "Transaction Monitoring",
//           icon: <CastOutlined />,
//           to: getRegister(URL_TRANSACTION_MONITORING),
//         },
//         // {
//         //   label: "Reconciliation Panel",
//         //   icon: <PanoramaHorizontalOutlined />,
//         //   to: "",
//         // },
//         {
//           label: "Reconciliation Statements",
//           icon: <AssignmentOutlined />,
//           to: getRegister(URL_RECONCILIATION_STATEMENTS),
//         },
//         {
//           label: "Partner Item Mapping",
//           icon: <ThreePOutlined />,
//           to: getRegister(URL_PARTNER_ITEM_MAPPING),
//         },
//         {
//           label: "Refunds",
//           icon: <SettingsBackupRestoreOutlined />,
//           to: getRegister(URL_REVERSE_TRANSACTIONS),
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         {
//           label: "Mark Transaction",
//           icon: <BookmarkAddOutlined />,
//           to: getRegister(URL_MARK_TRANSACTION),
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//       ],
//     },
//     {
//       label: "Revenue & Settlements",
//       icon: <DatasetLinkedOutlined />,
//       children: [
//         // {
//         //   label: "Revenue Breakdown",
//         //   icon: <AssessmentOutlined />,
//         //   to: "",
//         // },
//         // {
//         //   label: "Settlement Tracker",
//         //   icon: <TimelineOutlined />,
//         //   to: "",
//         // },
//         // {
//         //   label: "Commission Module",
//         //   icon: <UpcomingOutlined />,
//         //   to: "",
//         // },
//         // {
//         //   label: "Bank Reconciliation",
//         //   icon: <AssuredWorkloadOutlined />,
//         //   to: "",
//         // },
//         {
//           label: "Replenishment",
//           icon: <RedeemOutlined />,
//           to: getRegister(URL_REPLENISHMENT),
//         },
//         {
//           label: "Credit Holdings",
//           icon: <CreditScoreOutlined />,
//           to: getRegister(URL_CREDIT_HOLDINGS),
//         },
//         {
//           label: "Liquidations",
//           icon: <RedeemOutlined />,
//           to: getRegister(URL_LIQUIDATIONS),
//         },
//         {
//           label: "Debit Holdings",
//           icon: <CreditScoreOutlined />,
//           to: getRegister(URL_DEBIT_HOLDINGS),
//         },
//         {
//           label: "Monitored Accounts",
//           icon: <TvOutlined />,
//           to: getRegister(URL_MONITORED_ACCOUNTS),
//         },
//         {
//           label: "Account Alerts",
//           icon: <NotificationsActiveOutlined />,
//           to: getRegister(URL_ACCOUNT_ALERTS),
//         },
//         {
//           label: "Credit Holding Types",
//           icon: <CreditScoreOutlined />,
//           to: getRegister(URL_CREDIT_HOLDING_TYPES),
//         },
//         {
//           label: "Debit Holding Types",
//           icon: <CreditScoreOutlined />,
//           to: getRegister(URL_DEBIT_HOLDINGS_TYPES),
//         },
//         {
//           label: "Liquidation Bank Codes",
//           icon: <AccountBalanceOutlined />,
//           to: getRegister(URL_LIQUIDATION_BANK_CODES),
//         },
//         {
//           label: "Liquidation Bank Accounts",
//           icon: <AccountBalanceOutlined />,
//           to: getRegister(URL_BANK_ACCOUNTS),
//         },
//       ],
//     },
//     {
//       label: "Accounting",
//       icon: <AccountTreeOutlined />,
//       visible: itemExists(permissions, [CAN_ADMIN]),
//       children: [
//         {
//           label: "Chart of Accounts",
//           icon: <StackedBarChartOutlined />,
//           to: getRegister(URL_CHARTS_CONFIGURABLES),
//         },
//         {
//           label: "Payment Items",
//           icon: <CategoryOutlined />,
//           to: getRegister(URL_PAYMENT_ITEMS),
//           // visible: hasConfigurablesPermissions,
//         },
//         {
//           label: "Payment Types",
//           icon: <CategoryOutlined />,
//           to: getRegister(URL_PAYMENT_TYPES),
//           // visible: hasConfigurablesPermissions,
//         },
//         {
//           label: "Charge Assignment",
//           icon: <CashOutline />,
//           to: getRegister(URL_CHARGE_ASSIGNMENT),
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         {
//           label: "Partners",
//           icon: <PeopleAltOutlined />,
//           to: getRegister(URL_PARTNERS),
//           // visible: hasConfigurablesPermissions,
//         },
//         {
//           label: "Partners charges",
//           icon: <PeopleAltOutlined />,
//           to: getRegister(URL_PARTNER_CHARGES),
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         {
//           label: "System Wallets",
//           icon: <PeopleAltOutlined />,
//           to: getRegister(URL_SYSTEM_WALLETS),
//         },
//       ],
//     },
//     {
//       label: "Finance & Treasury",
//       icon: <MoneyOffOutlined />,
//       visible: itemExists(permissions, [CAN_ADMIN]),
//       children: [
//         {
//           label: "Revenue Sharing",
//           icon: <ShareOutlined />,
//           children: [
//             {
//               label: "Float Interest Statements",
//               icon: <SummarizeOutlined />,
//               to: getRegister(URL_FLOAT_INTEREST_STATEMENTS),
//             },
//             {
//               label: "Float Interest Configs",
//               icon: <SettingsSuggestOutlined />,
//               to: getRegister(URL_FLOAT_INTEREST_CONFIGS),
//             },
//           ],
//         },
//         {
//           label: "Settlement Engine",
//           icon: <EngineeringOutlined />,
//           children: [
//             {
//               label: "Eligibility Review",
//               icon: <NotListedLocationOutlined />,
//               click: () => {
//                 setOpenSettlementEligibilityReview();
//               },
//             },
//             {
//               label: "Batches",
//               icon: <BatchPredictionOutlined />,
//               children: [
//                 {
//                   label: "All Batches",
//                   icon: <BatchPredictionOutlined />,
//                   to: getRegister(URL_SETTLEMENT_BATCHES),
//                 },
//                 {
//                   label: "Draft",
//                   icon: <SaveAsOutlined />,
//                   to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${DRAFT}?status=${DRAFT}`,
//                 },
//                 {
//                   label: "Pending Approval",
//                   icon: <PendingOutlined />,
//                   to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${PENDING_APPROVAL}?status=${PENDING_APPROVAL}`,
//                 },
//                 {
//                   label: "Approved",
//                   icon: <VerifiedOutlined />,
//                   to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${APPROVED}?status=${APPROVED}`,
//                 },
//                 // {
//                 //   label: "Rejected",
//                 //   icon: <NotInterestedOutlined />,
//                 //   to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${REJECTED}?status=${REJECTED}`,
//                 // },
//                 {
//                   label: "Sent to Bank",
//                   icon: <SendOutlined />,
//                   to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${SENT_TO_BANK}?status=${SENT_TO_BANK}`,
//                 },
//                 {
//                   label: "Settled",
//                   icon: <CheckCircleOutline />,
//                   to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${SETTLED}?status=${SETTLED}`,
//                 },
//                 {
//                   label: "Failed / Retry",
//                   icon: <ErrorOutline />,
//                   to: `${getRegister(URL_SETTLEMENT_BATCHES)}/${FAILED}?status=${FAILED}`,
//                 },
//               ],
//             },
//             {
//               label: "Approvals",
//               icon: <ApprovalOutlined />,
//               children: [
//                 {
//                   label: "My Queue",
//                   icon: <QueueOutlined />,
//                   to: getRegister(URL_SETTLEMENT_APPROVALS),
//                 },
//                 {
//                   label: "Level 1 Treasury",
//                   icon: <ApprovalOutlined />,
//                   to: `${getRegister(URL_SETTLEMENT_APPROVALS)}?level=1`,
//                 },
//                 {
//                   label: "Level 2 Compliance",
//                   icon: <ApprovalOutlined />,
//                   to: `${getRegister(URL_SETTLEMENT_APPROVALS)}?level=2`,
//                 },
//                 {
//                   label: "Level 3 Finance Manager",
//                   icon: <ApprovalOutlined />,
//                   to: `${getRegister(URL_SETTLEMENT_APPROVALS)}?level=3`,
//                 },
//               ],
//             },
//             {
//               label: "Settlement Reports",
//               icon: <DescriptionOutlined />,
//               children: [
//                 {
//                   label: "Daily Settlement Report",
//                   icon: <DescriptionOutlined />,
//                   to: "/reports/daily-batch-settlement-report",
//                 },
//                 {
//                   label: "Compliance Report",
//                   icon: <DescriptionOutlined />,
//                   to: "/reports/compliance-report",
//                 },
//               ],
//             },
//             {
//               label: "Configurations",
//               icon: <SettingsSuggestOutlined />,
//               children: [
//                 {
//                   label: "Beneficiaries",
//                   icon: <PeopleAltOutlined />,
//                   to: getRegister(URL_SETTLEMENT_BENEFICIARIES),
//                 },
//                 {
//                   label: "Rules",
//                   icon: <WorkHistoryOutlined />,
//                   to: getRegister(URL_SETTLEMENT_RULES),
//                 },
//               ],
//             },
//           ],
//         },
//       ],
//     },
//     {
//       label: "Fraud Detection",
//       icon: <ReportOutlined />,
//       visible: itemExists(permissions, [CAN_ADMIN]),
//       children: [
//         {
//           label: "Fraud Dashboard",
//           icon: <GridOutline />,
//           to: URL_FRAUD_DASHBOARD,
//         },
//         // {
//         //   label: "Fraud Rules",
//         //   icon: <GavelOutlined />,
//         //   to: getRegister(URL_FRAUD_RULES),
//         // },
//         {
//           label: "Fraud Alerts",
//           icon: <BrowseGalleryOutlined />,
//           to: getRegister(URL_FRAUD_ALERTS),
//         },
//         {
//           label: "Fraud Cases",
//           icon: <CasesOutlined />,
//           to: getRegister(URL_FRAUD_CASES),
//         },
//         {
//           label: "Fraud Weight Score",
//           icon: <Scale />,
//           to: getRegister(URL_FRAUD_WEIGHTSCORE),
//         },
//         {
//           label: "Fraud Audit Logs",
//           icon: <ArticleOutlined />,
//           to: getRegister(URL_FRAUD_AUDIT_LOGS),
//         },
//         {
//           label: "Fraud Rule History",
//           icon: <WorkHistoryOutlined />,
//           to: getRegister(URL_FRAUD_RULES_HISTORY),
//         },
//         {
//           label: "Fraud Events",
//           icon: <EventOutlined />,
//           to: getRegister(URL_FRAUD_EVENTS),
//         },
//         {
//           label: "Fraud Templates",
//           icon: <EventOutlined />,
//           to: getRegister(URL_FRAUD_TEMPLATES),
//         },
//         {
//           label: "Fraud Configurations",
//           icon: <SettingsInputComponentOutlined />,
//           to: getRegister(URL_FRAUD_CONFIGURATIONS),
//         },
//       ],
//     },
//     // {
//     //   label: "Compliance & AML",
//     //   icon: <LocalPoliceOutlined />,
//     //   to: "",
//     // },
//     {
//       label: "App Menu Management",
//       icon: <PaymentOutlined />,
//       visible: itemExists(permissions, [CAN_ADMIN]),
//       to: getRegister(URL_PAYMENT_SERVICES),
//     },

//     {
//       label: "Wallet Transactions",
//       icon: <WalletOutline />,
//       children: [
//         {
//           label: "Top Up Wallet",
//           icon: <Add />,
//           // to: getForm("wallet_deposit"),
//           to: "/wallet-deposit/create",
//           visible: itemExists(permissions, [CAN_AGENT, CAN_MERCHANT]),
//         },
//         {
//           label: "Transfer",
//           icon: <NearMeOutlined />,
//           to: "/wallet-transfer/create",
//           visible: itemExists(permissions, [CAN_AGENT, CAN_MERCHANT]),
//         },
//         {
//           label: "Transactions",
//           icon: <SaveOutline />,
//           to: URL_WALLET_TRANSFERS,
//         },
//       ],
//     },
//     {
//       label: "Utilities",
//       icon: <LocalMallOutlined />,
//       children: [
//         {
//           label: "Make Utility Payment",
//           icon: <RequestQuoteOutlined />,
//           to: "/utility-payments/create",
//         },
//         {
//           label: "Utility Payments",
//           icon: <ListAltOutlined />,
//           to: getRegister(URL_UTILITY_TRANSACTIONS),
//         },
//         // {
//         //   label: "Check Payment Status",
//         //   icon: <PriceCheckOutlined />,
//         //   click: () => {
//         //     setOpenCheckPaymentStatus();
//         //   },
//         // },
//       ],
//     },
//     {
//       label: "E-Money Ledgers",
//       icon: <MoneyOutlined />,
//       visible: itemExists(permissions, [CAN_ADMIN]),
//       children: [
//         {
//           label: "General Ledger",
//           icon: <DocumentTextOutline />,
//           to: `${URL_LEDGERS}`,
//         },
//         {
//           label: "Merchant Sub Ledger",
//           icon: <DocumentTextOutline />,
//           to: "/reports/merchant-sub-ledgers",
//         },
//         {
//           label: "Agent Sub Ledger",
//           icon: <DocumentTextOutline />,
//           to: "/reports/agent-sub-ledgers",
//         },
//         {
//           label: "Public User Sub Ledger",
//           icon: <DocumentTextOutline />,
//           to: "/reports/public-user-sub-ledgers",
//         },
//         {
//           label: "Financial Sub Ledger",
//           icon: <DocumentTextOutline />,
//           to: "/reports/financial-sub-ledgers",
//         },
//       ],
//     },
//     {
//       label: "Reports",
//       icon: <LayersOutline />,
//       children: [
//         {
//           label: "Adhoc Reports",
//           icon: <DocumentTextOutline />,
//           to: "/reports/adhoc",
//           visible: itemExists(permissions, [
//             CAN_ADMIN,
//             CAN_AGENT,
//             CAN_MERCHANT,
//           ]),
//         },
//         {
//           label: "Chart of Accounts",
//           icon: <DocumentTextOutline />,
//           to: "/reports/chart-of-accounts-report",
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         // {
//         //   label: "Commissions",
//         //   icon: <DocumentTextOutline />,
//         //   to: "/reports/commissions",
//         //   visible: itemExists(permissions, [CAN_ADMIN]),
//         // },
//         {
//           label: "Transactions",
//           icon: <DocumentTextOutline />,
//           to: "/reports/transactions",
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         // {
//         //   label: "Settlement",
//         //   icon: <DocumentTextOutline />,
//         //   to: "/reports/settlement-report",
//         //   visible: itemExists(permissions, [CAN_ADMIN]),
//         // },
//         // {
//         //   label: "Commission Summary",
//         //   icon: <DocumentTextOutline />,
//         //   to: "/reports/commission-summary-report",
//         //   visible: itemExists(permissions, [
//         //     CAN_ADMIN,
//         //     CAN_AGENT,
//         //     CAN_MERCHANT,
//         //   ]),
//         // },
//         {
//           label: "Sales Summary",
//           icon: <PaidOutlined />,
//           visible: itemExists(permissions, [
//             CAN_ADMIN,
//             CAN_AGENT,
//             CAN_MERCHANT,
//           ]),
//           children: [
//             // {
//             //   label: "Merchant Summary",
//             //   icon: <DocumentTextOutline />,
//             //   to: "/reports/merchant-summary",
//             // },
//             // {
//             //   label: "Daily Summary",
//             //   icon: <DocumentTextOutline />,
//             //   to: "/reports/daily-summary",
//             // },
//             {
//               label: "Transaction Summary",
//               icon: <DocumentTextOutline />,
//               to: URL_TRANSACTION_SUMMARY,
//             },
//             {
//               label: "Merchant Summary",
//               icon: <DocumentTextOutline />,
//               // to: URL_MERCHANT_MONTHLY_REPORT,
//               to: URL_MERCHANT_SUMMARY,
//             },
//             {
//               label: "Payment Item Summary",
//               icon: <DocumentTextOutline />,
//               to: URL_PAYMENT_ITEM_SUMMARY,
//             },

//             // {
//             //   label: "Payments Summary",
//             //   icon: <DocumentTextOutline />,
//             //   to: "/reports/payments-summary",
//             // },
//           ],
//         },

//         {
//           label: "Commissions Summary",
//           icon: <PaymentsTwoTone />,
//           visible: itemExists(permissions, [
//             CAN_ADMIN,
//             CAN_AGENT,
//             CAN_MERCHANT,
//           ]),
//           children: [
//             {
//               label: "Partner Commission Summary",
//               icon: <DocumentTextOutline />,
//               to: URL_PARTNER_COMMISSION_SUMMARY,
//             },
//             {
//               label: "Agent Commission Summary",
//               icon: <DocumentTextOutline />,
//               to: URL_AGENT_COMMISSION_SUMMARY,
//             },
//             {
//               label: "Yoya Commission Summary",
//               icon: <DocumentTextOutline />,
//               to: URL_YOYA_COMMISSION_SUMMARY,
//             },
//           ],
//         },
//         {
//           label: "Channel Separations",
//           icon: <CallSplit />,
//           visible: itemExists(permissions, [
//             CAN_ADMIN,
//             CAN_AGENT,
//             CAN_MERCHANT,
//           ]),
//           children: [
//             {
//               label: "Transaction Channel Separation",
//               icon: <CurrencyExchange />,
//               to: URL_TRANSACTION_CHANNEL_SEPARATION,
//             },
//             {
//               label: "Payment Item Channel Report",
//               icon: <CurrencyExchange />,
//               to: URL_PAYMENT_ITEM_CHANNEL_REPORT,
//             },
//           ],
//         },
//         // {
//         //   label: "Commission Revenue",
//         //   icon: <DocumentTextOutline />,
//         //   to: "/reports/commission-revenue-report",
//         //   visible: itemExists(permissions, [CAN_ADMIN]),
//         // },
//         // {
//         //   label: "Transaction List",
//         //   icon: <DocumentTextOutline />,
//         //   to: "/reports/transaction-list-report",
//         //   visible: itemExists(permissions, [
//         //     CAN_ADMIN,
//         //     CAN_AGENT,
//         //     CAN_MERCHANT,
//         //   ]),
//         // },
//         {
//           label: "General Ledger",
//           icon: <DocumentTextOutline />,
//           to: "/reports/general-ledger-report",
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         {
//           label: "Profit and Loss",
//           icon: <DocumentTextOutline />,
//           to: "/reports/profit-and-loss-report",
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         // {
//         //   label: "Sales",
//         //   icon: <DocumentTextOutline />,
//         //   to: "/reports/sales-report",
//         //   visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
//         // },
//         {
//           label: "Trial Balance",
//           icon: <DocumentTextOutline />,
//           to: getRegister(URL_TRIAL_BALANCE),
//           visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT]),
//         },
//         // {
//         //   label: "Wallet Balance",
//         //   icon: <DocumentTextOutline />,
//         //   to: "/reports/wallet-balance-report",
//         //   visible: itemExists(permissions, [
//         //     CAN_ADMIN,
//         //     CAN_AGENT,
//         //     CAN_MERCHANT,
//         //   ]),
//         // },
//       ],
//     },
//     {
//       label: "System Reports",
//       icon: <PeopleOutline />,
//       visible: itemExists(permissions, [CAN_ADMIN, CAN_MERCHANT, CAN_AGENT]),
//       children: [
//         {
//           label: "Utility Logs",
//           icon: <ListSharp />,
//           to: `/reports/utility-logs-report`,
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         {
//           label: "External Call Logs",
//           icon: <LinearScale />,
//           to: `/reports/external-call-logs-report`,
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },

//         // {
//         //   label: "Transactions",
//         //   icon: <AccountBalanceWalletOutlined />,
//         //   to: `/reports/transactions-report`,
//         //   visible: itemExists(permissions, [
//         //     CAN_ADMIN,
//         //     CAN_MERCHANT,
//         //     CAN_AGENT,
//         //   ]),
//         // },
//         {
//           label: "Liquidation Logs",
//           icon: <RedeemOutlined />,
//           to: `/reports/liquidation-logs`,
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         {
//           label: "System Exceptions",
//           icon: <SettingsSystemDaydream />,
//           to: `/reports/system-exceptions-report`,
//           visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         {
//           label: "Customer Report Requests",
//           icon: <PeopleOutline />,
//           to: `/reports/customer-report-requests`,
//           visible: itemExists(permissions, [
//             CAN_ADMIN,
//             CAN_MERCHANT,
//             CAN_AGENT,
//           ]),
//         },
//       ],
//     },
//     // Support
//     {
//       label: "Audit Activity",
//       icon: <WorkHistoryOutlined />,
//       visible: itemExists(permissions, [CAN_ADMIN]) || is_staff,
//       children: [
//         // {
//         //   label: "All Audit Activities",
//         //   visible: itemExists(permissions, [CAN_ADMIN]),
//         //   icon: <AssignmentTurnedIn />,
//         //   to: getRegister(URL_AUDIT_ACTIVITY),
//         // },
//         {
//           label: "Security Activites",
//           visible: itemExists(permissions, [CAN_ADMIN]),
//           to: getRegister(URL_SECURITY_ACTIVITY),
//           icon: <SecurityOutlined />,
//         },
//         {
//           label: "Business Audit Logs",
//           visible: itemExists(permissions, [CAN_ADMIN]),
//           to: getRegister(URL_BUSINESS_AUDIT_LOGS),
//           icon: <SecurityOutlined />,
//         },
//       ],
//     },
//     // Support
//     {
//       label: "Support",
//       icon: <HelpOutline />,
//       children: [
//         {
//           label: "Enquiries",
//           icon: <ConfirmationNumberOutlined />,
//           to: getRegister(URL_CONTACT_US),
//           // visible: itemExists(permissions, [CAN_ADMIN]),
//         },
//         {
//           label: "Incident Ticketing",
//           icon: <BugReportOutlined />,

//           // visible: itemExists(permissions, [CAN_ADMIN]),
//           children: [
//             {
//               label: "Create Ticket",
//               icon: <Add />,
//               to: "/incident_ticketing/create",
//             },
//             {
//               label: "Ticket List",
//               icon: <ListAltOutlined />,
//               to: getRegister(URL_INCIDENT_TICKETING),
//               visible: itemExists(permissions, [CAN_ADMIN]),
//             },
//             {
//               label: "Raised Tickets",
//               icon: <ListAltOutlined />,
//               to: getRegister(URL_RAISED_TICKETS),
//             },
//             {
//               label: "Assigned Tickets",
//               icon: <ListAltOutlined />,
//               to: getRegister(URL_ASSIGNED_TICKETS),
//             },
//           ],
//         },
//         {
//           label: "Ticket Categorization",
//           icon: <CategoryOutlined />,

//           // visible: itemExists(permissions, [CAN_ADMIN]),
//           children: [
//             {
//               label: "Create Category",
//               icon: <Add />,
//               to: "/ticket_categorization/create",
//               // visible: itemExists(permissions, [CAN_ADMIN]),
//             },
//             {
//               label: "Ticket Categories",
//               icon: <ListAltOutlined />,
//               to: getRegister(URL_TICKET_CATEGORIZATION),
//               // visible: itemExists(permissions, [CAN_ADMIN]),
//             },
//           ],
//         },
//       ],
//     },

//     {
//       icon: <SettingsOutline />,
//       label: "Settings",
//       // visible: itemExists(permissions, [CAN_ADMIN]),
//       visible: true,
//       to: "/settings",
//     },
//   ];
//   return menu;
// }

// const findParentKeys = (
//   menu: any[],
//   targetPath: string,
//   parentKeys: string[] = [],
// ): string[] => {
//   const normalizedTargetPath = targetPath.split("?")[0];

//   for (const item of menu) {
//     const itemKey =
//       item.key || item.label?.toLowerCase().replace(/\s+/g, "-") || item.to;

//     const normalizedItemPath = item.to ? item.to.split("?")[0] : "";

//     if (normalizedItemPath && normalizedItemPath === normalizedTargetPath) {
//       return parentKeys;
//     }

//     if (item.children?.length) {
//       const found = findParentKeys(item.children, targetPath, [
//         ...parentKeys,
//         itemKey,
//       ]);
//       if (found.length > 0) {
//         return found;
//       }
//       if (
//         item.children.some((child: any) => {
//           const normalizedChildPath = child.to ? child.to.split("?")[0] : "";
//           return normalizedChildPath === normalizedTargetPath;
//         })
//       ) {
//         return [...parentKeys, itemKey];
//       }
//     }
//   }
//   return [];
// };

// export const LeftMenu: React.FC = () => {
//   const { data } = useGlobalContext();
//   const settingsItems = useSettings();
//   const leftMenuItems = LeftMenuItems();
//   const location = useLocation();

//   const menuSource = data?.showSettings ? settingsItems : leftMenuItems;

//   const [openKeys, setOpenKeys] = useState<string[]>([]);
//   const [selectedKeys, setSelectedKeys] = useState<string[]>(["default"]);

//   useEffect(() => {
//     const currentPath = location.pathname + location.search;
//     const normalizedCurrentPath = currentPath.split("?")[0];

//     const parentKeys = findParentKeys(menuSource, currentPath);

//     const flatMenu = flattenMenu(menuSource);

//     // First try to find exact match (including query params)
//     let currentItem = flatMenu.find((item: any) => item.to === currentPath);

//     // If no exact match, try normalized path match
//     if (!currentItem) {
//       currentItem = flatMenu.find((item: any) => {
//         const normalizedItemPath = item.to ? item.to.split("?")[0] : "";
//         return normalizedItemPath === normalizedCurrentPath;
//       });
//     }

//     const currentKey =
//       currentItem?.key ||
//       currentItem?.label?.toLowerCase().replace(/\s+/g, "-") ||
//       currentPath;

//     setOpenKeys((prev) => {
//       if (JSON.stringify(prev) === JSON.stringify(parentKeys)) return prev;
//       return parentKeys;
//     });

//     setSelectedKeys((prev) => {
//       const newKeys = [currentKey || "default"];
//       if (JSON.stringify(prev) === JSON.stringify(newKeys)) return prev;
//       return newKeys;
//     });
//   }, [location.pathname, location.search]);

//   const handleOpenChange = (keys: string[]) => {
//     setOpenKeys(keys);
//   };

//   const flatMenu = flattenMenu(menuSource);
//   useDynamicPageTitle(flatMenu);

//   return (
//     <Menu
//       className="font13 leftmenu radius6"
//       mode="inline"
//       openKeys={openKeys}
//       selectedKeys={selectedKeys}
//       onOpenChange={handleOpenChange}
//       theme="light"
//     >
//       {RecursiveMenu(menuSource)}
//     </Menu>
//   );
// };
